import React, { useEffect, useState, useCallback } from "react";
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import "./recruiters.css";
import DataTable from "../../components/DataTable/DataTable";

const AllapprovedRecruitersTable = ({
  allapprovedRecruiters,
  isDataLoaded,
  userType,
  setPaginationState,
  totalCount,
}) => {
  const history = useNavigate();

  const [dataResult, setDataResult] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [takeNumberOfResult, setTakeNumberOfResult] = useState(10);

  const handleRequestDateLuxon = (data) => {
    const date = data.approvalDate;
    const formatted = DateTime.fromISO(date)
      .toLocal()
      .startOf("day")
      .toJSDate();
    return formatted || "N/A";
  };

  const stylesforBadge = {
    textAlign: "center",
  };

  const handleRowClick = useCallback((data) => {
    if (data.role.roleName === "MIS Operator") {
      history("/dashboard", {
        state: {
          id: data?.id,
          roleName: data?.role?.roleName,
          recruitmentPartnerId: data?.recruitmentpartner?.id,
          userType: "Admin",
          userRecords: data,
          userRole: data?.role?.roleName
        },
      });
    } else {
      history("/dashboard", {
        state: {
          id: data?.id,
          roleName: data?.role?.roleName,
          recruitmentPartnerId: data?.recruitmentpartner?.id,
          userRecords: data
        },
      });
    }
  }, [history]);

  // Column definitions
  const columns = [
    {
      id: "action",
      header: "Action",
      cell: ({ row }) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleRowClick(row.original)}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: "500",
            fontSize: "13px",
            padding: "6px 16px",
            boxShadow: "0 2px 4px rgba(25, 118, 210, 0.2)",
            '&:hover': {
              boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
              transform: "translateY(-1px)"
            },
            transition: "all 0.2s ease"
          }}
        >
          Go To Profile
        </Button>
      ),
      size: 150,
      enableSorting: false,
    },
    {
      accessorKey: "firstName",
      header: "First Name",
      size: 180,
    },
    {
      accessorKey: "lastName",
      header: "Last Name",
      size: 180,
    },
    {
      accessorKey: "email",
      header: "Email",
      size: 300,
    },
    {
      accessorKey: "role.roleName",
      header: "User role",
      size: 200,
    },
    {
      accessorKey: "recruitmentpartner.name",
      header: "Recruitment Organization",
      size: 300,
    },
    {
      accessorKey: "agencyName",
      header: "Agency",
      size: 200,
    },
    {
      accessorKey: "agency.agencyType.name",
      header: "Agency Type",
      size: 200,
    },
    {
      accessorKey: "communitySize",
      header: "Community Size",
      size: 200,
    },
    {
      accessorKey: "phoneNumber",
      header: "Phone Number",
      size: 200,
    },
    {
      accessorKey: "country",
      header: "Country",
      size: 200,
    },
    {
      accessorKey: "requestDate",
      header: "Approved On",
      cell: ({ row }) => {
        const approvalDate = row.original.approvalDate;
        if (approvalDate) {
          return DateTime.fromISO(approvalDate).toFormat("MM/dd/yyyy").toString();
        }
        return "N/A";
      },
      size: 200,
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ row }) => (
        <div style={stylesforBadge}>
          {row.original.isActive ? (
            <Badge
              color="primary"
              badgeContent="Active"
              className="recruiter_badge"
            />
          ) : (
            <Badge
              color="secondary"
              badgeContent="Inactive"
              className="recruiter_badge"
            />
          )}
        </div>
      ),
      size: 200,
    },
  ];

  // Handle page change
  const handlePageChange = (newPage, newPageSize) => {
    setPageNumber(newPage);
    if (newPageSize && newPageSize !== takeNumberOfResult) {
      setTakeNumberOfResult(newPageSize);
    }
    setPaginationState({
      page: newPage,
      pageSize: newPageSize || takeNumberOfResult,
    });
  }

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setTakeNumberOfResult(newPageSize);
    setPageNumber(1);
    setPaginationState({
      page: 1,
      pageSize: newPageSize,
    });
  };

  useEffect(() => {
    if (!allapprovedRecruiters || allapprovedRecruiters.length === 0) {
      setDataResult([]);
      return;
    }

    const processedData = allapprovedRecruiters.map(recruiter => {
      const processed = { ...recruiter };

      processed.country = recruiter.profile?.locationCountry
        ? JSON.parse(recruiter.profile.locationCountry).name || ""
        : "";

      if (recruiter.role.roleName === "Admin") {
        processed.communitySize = "N/A";
        processed.agencyName = "N/A";
      } else {
        const candidates = recruiter.myCandidates || [];
        const uniqueCandidates = [
          ...new Map(candidates.map(item => [item.candidateid, item])).values(),
        ];
        processed.communitySize = uniqueCandidates.length;

        processed.requestDate = recruiter.approvalDate != null
          ? handleRequestDateLuxon(recruiter)
          : "N/A";

        processed.agencyName = recruiter.agency?.agencyName || "N/A";
      }

      return processed;
    });

    setDataResult(processedData);
  }, [allapprovedRecruiters]);


  return (
    <>
      <DataTable
        data={dataResult}
        columns={userType === "Admin" ? columns : columns.slice(1)} // Exclude action column for non-admin users
        isLoading={!isDataLoaded}
        totalRecords={totalCount}
        currentPage={pageNumber}
        pageSize={takeNumberOfResult}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        enableFiltering={false}
        enablePagination={true}
        enableSorting={false}
        pageSizeOptions={[10, 20, 50, 100]}
        isServerSide={true}
        noDataText="No results found !"
      />
    </>


  );
};

export default AllapprovedRecruitersTable;